import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, X } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  Label,
  Select,
  useOptionalToast,
  PageHeader,
} from '@nexply/ui';
import type { GeoPlaceDTO, MapMemberDTO, MapWithoutLocationDTO } from '@nexply/api-client';
import { api } from '../../lib/api.js';

// Default-Icon-Fix für Leaflet (URLs vom CDN)
const ICON_RETINA = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png';
const ICON_URL = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png';
const SHADOW_URL = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png';

// @ts-expect-error: internal Leaflet API for default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: ICON_RETINA,
  iconUrl: ICON_URL,
  shadowUrl: SHADOW_URL,
});

function FitBounds({ members }: { members: MapMemberDTO[] }) {
  const map = useMap();
  useEffect(() => {
    if (members.length === 0) return;
    const bounds = L.latLngBounds(members.map((m) => [m.lat, m.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [members, map]);
  return null;
}

/** Leaflets eingebaute Zoom-Buttons heißen "Zoom in/out" — hier die deutsche Variante. */
function GermanZoomControl() {
  const map = useMap();
  useEffect(() => {
    const ctl = L.control.zoom({ zoomInTitle: 'Vergrößern', zoomOutTitle: 'Verkleinern' });
    ctl.addTo(map);
    return () => {
      ctl.remove();
    };
  }, [map]);
  return null;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Profilbild bzw. Initialen als Marker — statt der anonymen blauen Standard-Nadel
 * erkennt man auf einen Blick, wer wo sitzt.
 */
function memberIcon(m: MapMemberDTO): L.DivIcon {
  const initials =
    m.name
      .trim()
      .split(/\s+/)
      .map((p) => p[0] ?? '')
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  const inner = m.photoUrl
    ? `<img src="${escapeHtml(m.photoUrl)}" alt="" />`
    : `<span>${escapeHtml(initials)}</span>`;
  return L.divIcon({
    className: 'nexply-marker',
    html: `<div class="nexply-marker-pin">${inner}</div><div class="nexply-marker-tip"></div>`,
    iconSize: [38, 47],
    iconAnchor: [19, 45],
    popupAnchor: [0, -42],
  });
}

/** Verzögert einen Wert — die Ortssuche feuert erst, wenn kurz nicht mehr getippt wurde. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function MapPage() {
  const q = useQuery({ queryKey: ['map'], queryFn: () => api.map.get() });
  const [tag, setTag] = useState('');
  const [industry, setIndustry] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 250);
  // Welches Mitglied gerade den Ortssuche-Editor offen hat (max. eines).
  const [locatingId, setLocatingId] = useState<string | null>(null);

  const all = q.data?.members ?? [];
  const withoutLocation = q.data?.withoutLocation ?? [];

  const tagOptions = useMemo(() => {
    const s = new Set<string>();
    for (const m of all) for (const t of m.tags) s.add(t);
    return Array.from(s).sort();
  }, [all]);

  const industryOptions = useMemo(() => {
    const s = new Set<string>();
    for (const m of all) if (m.industry) s.add(m.industry);
    return Array.from(s).sort();
  }, [all]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    return all.filter((m) => {
      if (tag && !m.tags.includes(tag)) return false;
      if (industry && m.industry !== industry) return false;
      if (term) {
        const hay = [m.name, m.company, m.city, m.country]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    // Die Karte fliegt über FitBounds automatisch zu den Treffern.
  }, [all, tag, industry, debouncedSearch]);

  const center: [number, number] = filtered[0]
    ? [filtered[0].lat, filtered[0].lng]
    : [51.1657, 10.4515];

  // Icons pro Mitglied memoizen — sonst baut jeder Render alle Marker neu.
  const icons = useMemo(() => new Map(filtered.map((m) => [m.id, memberIcon(m)] as const)), [filtered]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Community-Karte"
        subtitle={
          q.isLoading
            ? 'Lädt …'
            : `${filtered.length} ${filtered.length === 1 ? 'Mitglied' : 'Mitglieder'} sichtbar${
                filtered.length !== all.length ? ` (von ${all.length})` : ''
              }`
        }
      />

      <Card>
        {/* CardBody bringt kein pt mit (ist für die Kombination mit CardHeader gedacht) —
            ohne pt-5 klebt der Inhalt oben an der Kante. */}
        <CardBody className="space-y-3 pt-5">
          <div>
            <Label>Suche</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder="Nach Name, Firma oder Standort suchen — die Karte fliegt zu den Treffern"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Tag</Label>
              <Select value={tag} onChange={(e) => setTag(e.currentTarget.value)}>
                <option value="">Alle Tags</option>
                {tagOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Branche</Label>
              <Select value={industry} onChange={(e) => setIndustry(e.currentTarget.value)}>
                <option value="">Alle Branchen</option>
                {industryOptions.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Select>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        <Card>
          <CardBody className="p-0 overflow-hidden">
            {q.isLoading ? (
              <div className="h-[520px] bg-raise animate-pulse" />
            ) : all.length === 0 ? (
              <div className="p-6 space-y-4">
                <EmptyState
                  icon="🗺️"
                  title="Keine Mitglieder mit Standort"
                  description={
                    withoutLocation.length > 0
                      ? `${withoutLocation.length} Mitglieder haben noch keine Koordinaten. Wir können sie aus Stadt und Land ermitteln.`
                      : 'Ergänze Stadt und Land bei deinen Mitgliedern, um sie auf der Karte zu sehen.'
                  }
                />
                {withoutLocation.length > 0 && <GeocodeButton />}
              </div>
            ) : (
              <div className="h-[520px] w-full">
                <MapContainer
                  center={center}
                  zoom={5}
                  scrollWheelZoom
                  zoomControl={false}
                  style={{ height: '100%', width: '100%' }}
                >
                  {/* Deutscher OSM-Kachelserver: beschriftet Orte mit deutschen Namen
                      (Warschau statt Warszawa), Rest der Welt bleibt lesbar. */}
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>-Mitwirkende'
                    url="https://tile.openstreetmap.de/{z}/{x}/{y}.png"
                  />
                  <GermanZoomControl />
                  <FitBounds members={filtered} />
                  {filtered.map((m) => (
                    <Marker key={m.id} position={[m.lat, m.lng]} icon={icons.get(m.id) ?? memberIcon(m)}>
                      <Popup>
                        <div className="font-semibold">{m.name}</div>
                        {m.company && <div className="text-text-dim">{m.company}</div>}
                        {(m.city || m.country) && (
                          <div className="text-text-muted text-[11px] mt-1">
                            {[m.city, m.country].filter(Boolean).join(', ')}
                          </div>
                        )}
                        {m.industry && (
                          <div className="text-text-muted text-[11px]">{m.industry}</div>
                        )}
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody className="pt-5">
            <h2 className="text-sm font-medium text-text mb-1">
              Ohne Standort
              <span className="ml-2 text-xs text-text-muted">({withoutLocation.length})</span>
            </h2>
            {withoutLocation.length === 0 ? (
              <div className="text-xs text-text-muted mt-2">Alle Mitglieder verortet.</div>
            ) : (
              <div className="space-y-3">
                <p className="text-[11px] text-text-muted leading-relaxed">
                  Klicke ein Mitglied an, um seinen Standort zu suchen und zu setzen.
                </p>
                <GeocodeButton />
                <ul className="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
                  {withoutLocation.map((m) => (
                    <LocateMemberRow
                      key={m.id}
                      member={m}
                      open={locatingId === m.id}
                      onToggle={() => setLocatingId(locatingId === m.id ? null : m.id)}
                      onSaved={() => setLocatingId(null)}
                    />
                  ))}
                </ul>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

/**
 * Eine Zeile in "Ohne Standort": Klick klappt eine Ortssuche auf. Die Treffer kommen
 * serverseitig von Nominatim (deutsch beschriftet); ein Klick auf einen Treffer speichert
 * Stadt, Land und exakte Koordinaten am Mitglied — der Punkt erscheint sofort auf der Karte.
 */
function LocateMemberRow({
  member,
  open,
  onToggle,
  onSaved,
}: {
  member: MapWithoutLocationDTO;
  open: boolean;
  onToggle: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();
  const toast = useOptionalToast();
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, 400);
  const query = debounced.trim();

  const search = useQuery({
    queryKey: ['geo-search', query],
    queryFn: () => api.map.geoSearch(query),
    enabled: open && query.length >= 2,
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: (p: GeoPlaceDTO) =>
      api.members.update(member.id, {
        city: p.city ?? p.label,
        country: p.country,
        lat: p.lat,
        lng: p.lng,
      }),
    onSuccess: (_res, p) => {
      toast?.show(`${member.name} ist jetzt in ${p.city ?? p.label} verortet.`);
      onSaved();
      void qc.invalidateQueries({ queryKey: ['map'] });
    },
    onError: () => toast?.show('Standort konnte nicht gespeichert werden.'),
  });

  const results = search.data?.results ?? [];

  return (
    <li className="rounded-md border border-line bg-raise text-xs overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left transition hover:bg-surface-3"
      >
        <span className="min-w-0">
          <span className="block truncate text-text">{member.name}</span>
          {member.company && <span className="block truncate text-text-muted">{member.company}</span>}
        </span>
        {open ? (
          <X className="h-3.5 w-3.5 shrink-0 text-text-muted" />
        ) : (
          <MapPin className="h-3.5 w-3.5 shrink-0 text-gold" />
        )}
      </button>

      {open && (
        <div className="space-y-1.5 border-t border-line px-2.5 py-2">
          <input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.currentTarget.value)}
            placeholder="Stadt suchen …"
            className="w-full rounded-md border border-line bg-surface-2 px-2 py-1.5 text-xs text-text placeholder:text-text-muted outline-none focus:border-gold/50"
          />
          {search.isFetching && <div className="text-[11px] text-text-muted">Suche …</div>}
          {!search.isFetching && query.length >= 2 && results.length === 0 && (
            <div className="text-[11px] text-text-muted">Nichts gefunden — anders schreiben?</div>
          )}
          {results.map((p, i) => (
            <button
              key={`${p.lat}-${p.lng}-${i}`}
              type="button"
              disabled={save.isPending}
              onClick={() => save.mutate(p)}
              className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-text-dim transition hover:bg-surface-3 hover:text-text disabled:opacity-50"
            >
              <MapPin className="h-3 w-3 shrink-0 text-gold" />
              <span className="truncate">{p.label}</span>
            </button>
          ))}
        </div>
      )}
    </li>
  );
}

/**
 * Trägt fehlende Koordinaten nach. Das Geocoding läuft serverseitig im Hintergrund und ist
 * auf 1 Anfrage/Sekunde gedrosselt (Nominatim-Richtlinie) — die Punkte erscheinen also nach
 * und nach, nicht sofort. Darum lädt der Button die Karte nach kurzer Wartezeit neu.
 */
function GeocodeButton() {
  const qc = useQueryClient();
  const toast = useOptionalToast();

  const run = useMutation({
    mutationFn: () => api.members.geocodeMissing(),
    onSuccess: ({ queued }) => {
      toast?.show(
        queued === 0
          ? 'Keine Mitglieder mit Stadt ohne Koordinaten gefunden.'
          : `${queued} Standorte werden im Hintergrund ermittelt …`,
      );
      const delay = Math.min(queued * 1200 + 2000, 20000);
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['map'] });
      }, delay);
    },
  });

  return (
    <Button
      size="sm"
      variant="secondary"
      className="w-full"
      disabled={run.isPending}
      onClick={() => run.mutate()}
    >
      {run.isPending ? 'Wird ermittelt …' : 'Standorte nachtragen'}
    </Button>
  );
}
